import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'src/components/ui/Button';
import { Modal, ModalBody, ModalHeader } from 'src/components/ui/Modal';
import ReactMarkdown from 'react-markdown';
import FileApi from 'src/api/FileApi';
import Icon from 'src/components/Icon';

interface SkillFileListFieldSchema {
  title?: string;
  accept?: string;
  description?: string;
}

interface SkillFileListFieldIdSchema {
  $id: string;
}

interface SkillFileListFieldProps {
  formData: number[];
  schema?: SkillFileListFieldSchema;
  idSchema?: SkillFileListFieldIdSchema;
  onChange: (value: number[]) => void;
}

interface FileMeta {
  id: number;
  name: string;
}

interface PreviewState {
  id: number;
  name: string;
  content: string | null;
  loading: boolean;
  error: string | null;
}

function SkillFileListField(props: SkillFileListFieldProps) {
  const {
    formData,
    schema: { title, accept = '.md,text/markdown', description } = {},
    idSchema: { $id: fieldId } = { $id: '' },
    onChange: propOnChange,
  } = props;

  const ids = Array.isArray(formData) ? formData.filter(v => typeof v === 'number') : [];
  const inputRef = useRef<HTMLInputElement>(null);
  const [metaById, setMetaById] = useState<Record<number, FileMeta>>({});
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);

  useEffect(() => {
    let cancelled = false;
    const missing = ids.filter(id => !metaById[id]);
    if (!missing.length) return undefined;

    Promise.all(
      missing.map(id =>
        FileApi.getFile({ fileId: id })
          .then(data => ({ id, name: data?.fileName || data?.name || `File #${id}` }))
          .catch(() => ({ id, name: `File #${id} (missing)` }))
      )
    ).then(results => {
      if (cancelled) return;
      setMetaById(prev => {
        const next = { ...prev };
        results.forEach(m => {
          next[m.id] = m;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(',')]);

  async function openPreview(id: number) {
    const meta = metaById[id];
    const name = meta?.name || `File #${id}`;
    setPreview({ id, name, content: null, loading: true, error: null });
    try {
      const data = await FileApi.getFile({ fileId: id });
      const url = data?.url || data?.URL || null;
      if (!url) throw new Error(`File has no content URL.`);
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const text = await resp.text();
      setPreview({ id, name: data?.fileName || name, content: text, loading: false, error: null });
    } catch (e) {
      setPreview({ id, name, content: null, loading: false, error: e instanceof Error ? e.message : `Failed to load file.` });
    }
  }

  async function handleFile(file: File) {
    setError(null);
    if (!/\.md$/i.test(file.name) && file.type !== 'text/markdown') {
      setError(`Only .md files are allowed.`);
      return;
    }
    setUploading(true);
    try {
      const data = await FileApi.uploadFile({ file });
      const id = data?.id;
      if (!id) throw new Error('Upload response missing id.');
      setMetaById(prev => ({ ...prev, [id]: { id, name: data?.name || data?.fileName || file.name } }));
      propOnChange([...ids, id]);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Upload failed.`);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function removeAt(idx: number) {
    const next = [...ids];
    next.splice(idx, 1);
    propOnChange(next);
  }

  return (
    <div className="space-y-1">
      {title ? (
        <label className="control-label" htmlFor={fieldId}>
          {title}
        </label>
      ) : null}
      <div className="border theme-border rounded-xl p-2 mb-1 bg-zinc-50 dark:bg-zinc-900/40">
        {ids.length === 0 ? (
          <div className="text-zinc-400 text-xs italic">{`No skill files attached.`}</div>
        ) : (
          ids.map((id, idx) => {
            const meta = metaById[id];
            return (
              <div key={`${id}-${idx}`} className="flex items-center justify-between py-1">
                <span className="truncate mr-2 theme-text-primary text-sm">
                  <Icon icon="fa-file-alt" className="mr-2 text-zinc-400" />
                  {meta?.name || `File #${id}`}
                </span>
                <Button
                  color="link"
                  size="sm"
                  className="text-danger p-0"
                  onClick={e => {
                    e.preventDefault();
                    removeAt(idx);
                  }}
                  title={`Remove skill`}
                >
                  <Icon icon="X" weight="regular" />
                </Button>
              </div>
            );
          })
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          color="primary"
          size="sm"
          outline
          disabled={uploading}
          onClick={e => {
            e.preventDefault();
            inputRef.current?.click();
          }}
        >
          <Icon icon={uploading ? 'fa-spinner fa-spin' : 'fa-plus'} className="mr-1" />
          {uploading ? `Uploading…` : `Add skill (.md)`}
        </Button>
        <input
          ref={inputRef}
          id={fieldId}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
      </div>
      {error ? <div className="text-danger text-xs mt-1">{error}</div> : null}
      {description ? <div className="text-zinc-400 text-xs mt-1">{description}</div> : null}
      {preview ? (
        <Modal isOpen toggle={() => setPreview(null)}>
          <ModalHeader toggle={() => setPreview(null)}>{preview.name}</ModalHeader>
          <ModalBody>
            {preview.loading ? (
              <div className="text-zinc-400 text-sm">Loading…</div>
            ) : preview.error ? (
              <div className="text-danger text-sm">{preview.error}</div>
            ) : (
              <ReactMarkdown>{preview.content ?? ''}</ReactMarkdown>
            )}
          </ModalBody>
        </Modal>
      ) : null}
    </div>
  );
}

export default SkillFileListField;
